import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@mh-wms/api-client';
import { toast } from 'sonner';
import { authedFetch } from '@/shared/api/client';
import { useAuth } from '@/shared/auth/AuthProvider';

export type TableColumnSizes = Record<string, Record<string, number>>;
export type TableColumnOrders = Record<string, string[]>;
export interface LogisticsDeliveryDefaultFilters {
  search?: string;
  bucketIds?: string[];
  status?: string;
  sourceResource?: string;
  assignmentStatus?: string;
  bookingDateFrom?: string;
  bookingDateTo?: string;
  emptyDeliveryDate?: boolean;
  agent?: string;
  salesTeam?: string;
}

interface UserPreferencesValue {
  tableColumnSizes: TableColumnSizes;
  tableColumnOrders: TableColumnOrders;
  logisticsDeliveryDefaultFilters: LogisticsDeliveryDefaultFilters | null;
  preferencesLoaded: boolean;
  saveTableColumnSizes: (tableKey: string, columnSizes: Record<string, number>) => void;
  saveTableColumnOrder: (tableKey: string, columnOrder: string[]) => void;
  saveTableLayout: (
    tableKey: string,
    columnSizes: Record<string, number>,
    columnOrder: string[],
  ) => void;
  saveGlobalTableLayout: (
    tableKey: string,
    columnSizes: Record<string, number>,
    columnOrder: string[],
  ) => void;
  saveLogisticsDeliveryDefaultFilters: (filters: LogisticsDeliveryDefaultFilters) => void;
  clearLogisticsDeliveryDefaultFilters: () => void;
}

const EMPTY_PREFERENCES: UserPreferencesValue = {
  tableColumnSizes: {},
  tableColumnOrders: {},
  logisticsDeliveryDefaultFilters: null,
  preferencesLoaded: false,
  saveTableColumnSizes: () => undefined,
  saveTableColumnOrder: () => undefined,
  saveTableLayout: () => undefined,
  saveGlobalTableLayout: () => undefined,
  saveLogisticsDeliveryDefaultFilters: () => undefined,
  clearLogisticsDeliveryDefaultFilters: () => undefined,
};

const UserPreferencesContext = createContext<UserPreferencesValue>(EMPTY_PREFERENCES);
const QUERY_KEY = ['current-user', 'preferences'] as const;

export function normalizeColumnSizes(columnSizes: Record<string, number>) {
  return Object.fromEntries(
    Object.entries(columnSizes)
      .filter(([column, width]) => column.length > 0 && Number.isFinite(width))
      .map(([column, width]) => [column, Math.round(Math.min(1200, Math.max(60, width)))]),
  );
}

interface UserTablePreferences {
  tableColumnSizes: TableColumnSizes;
  tableColumnOrders: TableColumnOrders;
  logisticsDeliveryDefaultFilters: LogisticsDeliveryDefaultFilters | null;
}

async function loadPreferences(): Promise<UserTablePreferences> {
  const response = await authedFetch('/api/User/current/preferences');
  if (!response.ok) throw ApiError.from(response, await response.json().catch(() => undefined));
  const result = (await response.json()) as {
    tableColumnSizes?: TableColumnSizes;
    tableColumnOrders?: TableColumnOrders;
    logisticsDeliveryDefaultFilters?: LogisticsDeliveryDefaultFilters | null;
  };
  return {
    tableColumnSizes: result.tableColumnSizes ?? {},
    tableColumnOrders: result.tableColumnOrders ?? {},
    logisticsDeliveryDefaultFilters: result.logisticsDeliveryDefaultFilters ?? null,
  };
}

async function saveTablePreference(
  tableKey: string,
  columnSizes?: Record<string, number>,
  columnOrder?: string[],
) {
  const response = await authedFetch('/api/User/current/preferences/table', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tableKey, columnSizes, columnOrder }),
  });
  if (!response.ok) throw ApiError.from(response, await response.json().catch(() => undefined));
}

async function saveGlobalTablePreference(
  tableKey: string,
  columnSizes: Record<string, number>,
  columnOrder: string[],
) {
  const response = await authedFetch('/api/User/current/preferences/table/global', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tableKey, columnSizes, columnOrder }),
  });
  if (!response.ok) throw ApiError.from(response, await response.json().catch(() => undefined));
}

async function saveLogisticsDeliveryDefaultFilter(filters: LogisticsDeliveryDefaultFilters) {
  const response = await authedFetch(
    '/api/User/current/preferences/logistics-deliveries/default-filter',
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filters),
    },
  );
  if (!response.ok) throw ApiError.from(response, await response.json().catch(() => undefined));
}

async function clearLogisticsDeliveryDefaultFilter() {
  const response = await authedFetch(
    '/api/User/current/preferences/logistics-deliveries/default-filter',
    { method: 'DELETE' },
  );
  if (!response.ok) throw ApiError.from(response, await response.json().catch(() => undefined));
}

export function UserPreferencesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const preferencesQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: loadPreferences,
    enabled: isAuthenticated,
    staleTime: Infinity,
  });
  const data = preferencesQuery.data ?? {
    tableColumnSizes: {},
    tableColumnOrders: {},
    logisticsDeliveryDefaultFilters: null,
  };
  const mutation = useMutation({
    mutationFn: ({
      tableKey,
      columnSizes,
      columnOrder,
    }: {
      tableKey: string;
      columnSizes?: Record<string, number>;
      columnOrder?: string[];
    }) => saveTablePreference(tableKey, columnSizes, columnOrder),
    onError: () => toast.error('Table layout could not be saved.'),
  });
  const personalTableMutation = useMutation({
    mutationFn: ({
      tableKey,
      columnSizes,
      columnOrder,
    }: {
      tableKey: string;
      columnSizes: Record<string, number>;
      columnOrder: string[];
    }) => saveTablePreference(tableKey, columnSizes, columnOrder),
    onSuccess: () => toast.success('Table layout saved for you.'),
    onError: () => toast.error('Table layout could not be saved.'),
  });
  const defaultFilterMutation = useMutation({
    mutationFn: (filters: LogisticsDeliveryDefaultFilters | null) =>
      filters
        ? saveLogisticsDeliveryDefaultFilter(filters)
        : clearLogisticsDeliveryDefaultFilter(),
    onSuccess: (_, filters) =>
      toast.success(
        filters ? 'Default delivery filter saved.' : 'Default delivery filter cleared.',
      ),
    onError: () => toast.error('Default delivery filter could not be saved.'),
  });
  const globalTableMutation = useMutation({
    mutationFn: ({
      tableKey,
      columnSizes,
      columnOrder,
    }: {
      tableKey: string;
      columnSizes: Record<string, number>;
      columnOrder: string[];
    }) => saveGlobalTablePreference(tableKey, columnSizes, columnOrder),
    onSuccess: () => toast.success('Global table layout saved.'),
    onError: () => toast.error('Global table layout could not be saved.'),
  });

  const value = useMemo<UserPreferencesValue>(
    () => ({
      tableColumnSizes: data.tableColumnSizes,
      tableColumnOrders: data.tableColumnOrders,
      logisticsDeliveryDefaultFilters: data.logisticsDeliveryDefaultFilters,
      preferencesLoaded: preferencesQuery.isSuccess,
      saveTableColumnSizes(tableKey, columnSizes) {
        queryClient.setQueryData<UserTablePreferences>(QUERY_KEY, (current) => ({
          tableColumnSizes: { ...(current?.tableColumnSizes ?? {}), [tableKey]: columnSizes },
          tableColumnOrders: current?.tableColumnOrders ?? {},
          logisticsDeliveryDefaultFilters: current?.logisticsDeliveryDefaultFilters ?? null,
        }));
        mutation.mutate({ tableKey, columnSizes: normalizeColumnSizes(columnSizes) });
      },
      saveTableColumnOrder(tableKey, columnOrder) {
        const normalized = [...new Set(columnOrder.filter(Boolean))];
        queryClient.setQueryData<UserTablePreferences>(QUERY_KEY, (current) => ({
          tableColumnSizes: current?.tableColumnSizes ?? {},
          tableColumnOrders: { ...(current?.tableColumnOrders ?? {}), [tableKey]: normalized },
          logisticsDeliveryDefaultFilters: current?.logisticsDeliveryDefaultFilters ?? null,
        }));
        mutation.mutate({ tableKey, columnOrder: normalized });
      },
      saveTableLayout(tableKey, columnSizes, columnOrder) {
        const sizes = normalizeColumnSizes(columnSizes);
        const order = [...new Set(columnOrder.filter(Boolean))];
        queryClient.setQueryData<UserTablePreferences>(QUERY_KEY, (current) => ({
          tableColumnSizes: { ...(current?.tableColumnSizes ?? {}), [tableKey]: sizes },
          tableColumnOrders: { ...(current?.tableColumnOrders ?? {}), [tableKey]: order },
          logisticsDeliveryDefaultFilters: current?.logisticsDeliveryDefaultFilters ?? null,
        }));
        personalTableMutation.mutate({ tableKey, columnSizes: sizes, columnOrder: order });
      },
      saveGlobalTableLayout(tableKey, columnSizes, columnOrder) {
        globalTableMutation.mutate({
          tableKey,
          columnSizes: normalizeColumnSizes(columnSizes),
          columnOrder: [...new Set(columnOrder.filter(Boolean))],
        });
      },
      saveLogisticsDeliveryDefaultFilters(filters) {
        queryClient.setQueryData<UserTablePreferences>(QUERY_KEY, (current) => ({
          tableColumnSizes: current?.tableColumnSizes ?? {},
          tableColumnOrders: current?.tableColumnOrders ?? {},
          logisticsDeliveryDefaultFilters: filters,
        }));
        defaultFilterMutation.mutate(filters);
      },
      clearLogisticsDeliveryDefaultFilters() {
        queryClient.setQueryData<UserTablePreferences>(QUERY_KEY, (current) => ({
          tableColumnSizes: current?.tableColumnSizes ?? {},
          tableColumnOrders: current?.tableColumnOrders ?? {},
          logisticsDeliveryDefaultFilters: null,
        }));
        defaultFilterMutation.mutate(null);
      },
    }),
    [
      data,
      defaultFilterMutation,
      globalTableMutation,
      mutation,
      personalTableMutation,
      preferencesQuery.isSuccess,
      queryClient,
    ],
  );

  return (
    <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>
  );
}

export function useUserPreferences(): UserPreferencesValue {
  return useContext(UserPreferencesContext);
}
